import { Controller } from "@hotwired/stimulus";

import logger from "~/lib/logger";
import { DESKTOP_MIN_WIDTH, prefersReducedMotion } from "~/lib/viewport";

interface Breakpoint {
  /** Cell size in px. */
  cell: number;
  /** Falloff length as a fraction of the height. */
  ly: number;
}

interface Drift {
  /** How far the peak moves in from the right edge and down from the top. */
  x: number;
  y: number;
  /** Scale applied to both falloff lengths. */
  spread: number;
}

interface Field {
  desktop: Breakpoint;
  mobile: Breakpoint;
  /** Falloff length as a fraction of the width, and the exponent on the radius. */
  lx: number;
  power: number;
  peak: number;
  glow: readonly [number, number, number];
  drifts: readonly Drift[];
}

/** The first layer of every field: the peak at the corner itself. */
const STILL: Drift = { x: 0, y: 0, spread: 1 };

const FIELDS: Record<string, Field> = {
  pricing: {
    desktop: { cell: 34, ly: 0.26 },
    mobile: { cell: 20, ly: 0.123 },
    lx: 0.44,
    power: 3,
    peak: 0.32,
    glow: [161, 161, 161],
    drifts: [],
  },

  hero: {
    desktop: { cell: 28, ly: 0.238 },
    mobile: { cell: 20, ly: 0.238 },
    lx: 0.247,
    power: 1,
    peak: 0.58,
    glow: [139, 205, 255],
    drifts: [
      { x: 0.104, y: 0.022, spread: 1.16 },
      { x: 0.026, y: 0.088, spread: 0.86 },
    ],
  },
};

/** Below this the cell would not show at all, so it is left transparent. */
const FLOOR = 0.002;

const log = logger({ name: "WebPatternController" });

/**
 * The quantized glow in the corner of the pricing section and behind the /why
 * hero.
 *
 * One pixel per cell on a canvas, upscaled with nearest-neighbor, so a mosaic
 * costs one node rather than one per cell.
 */
export default class WebPatternController extends Controller<HTMLElement> {
  static override values = { field: { type: String, default: "pricing" } };
  declare readonly fieldValue: string;

  private canvases: HTMLCanvasElement[] = [];
  private observer: ResizeObserver | null = null;

  override connect(): void {
    if (FIELDS[this.fieldValue] === undefined) {
      log.warn(`no field named "${this.fieldValue}"`);
      return;
    }
    const observer = new ResizeObserver(() => this.draw());
    observer.observe(this.element);
    this.observer = observer;
    this.draw();
  }

  override disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.clear();
  }

  private clear(): void {
    for (const canvas of this.canvases) canvas.remove();
    this.canvases = [];
  }

  private draw(): void {
    const field = FIELDS[this.fieldValue];
    if (field === undefined) return;
    const width = this.element.clientWidth;
    const height = this.element.clientHeight;
    if (!width || !height) return;

    const isDesktop = window.innerWidth >= DESKTOP_MIN_WIDTH;
    /* A drift differs from the still layer only once its opacity animates, so
       below the breakpoint only the still layer is drawn. */
    const animated = isDesktop && !prefersReducedMotion();
    const layers = animated ? [STILL, ...field.drifts] : [STILL];
    if (this.canvases.length !== layers.length) {
      this.clear();
      this.canvases = layers.map(() =>
        this.element.appendChild(document.createElement("canvas")),
      );
    }
    const { cell, ly } = isDesktop ? field.desktop : field.mobile;
    layers.forEach((drift, index) => {
      const canvas = this.canvases[index];
      if (canvas === undefined) return;
      this.paint(canvas, { field, drift, cell, ly, width, height });
    });
  }

  private paint(
    canvas: HTMLCanvasElement,
    {
      field,
      drift,
      cell,
      ly,
      width,
      height,
    }: {
      field: Field;
      drift: Drift;
      cell: number;
      ly: number;
      width: number;
      height: number;
    },
  ): void {
    const cols = Math.ceil(width / cell) + 1;
    const rows = Math.ceil(height / cell) + 1;

    canvas.width = cols;
    canvas.height = rows;
    // The grid is anchored to the right edge, so the partial cell falls off the
    // left where the section's overflow clips it.
    canvas.style.width = `${cols * cell}px`;
    canvas.style.height = `${rows * cell}px`;

    const context = canvas.getContext("2d");
    if (context === null) return;
    const image = context.createImageData(cols, rows);
    const { data } = image;

    const peakX = width - drift.x * width;
    const peakY = drift.y * height;
    const rx = field.lx * drift.spread * width;
    const ry = ly * drift.spread * height;

    for (let row = 0; row < rows; row++) {
      for (let column = 0; column < cols; column++) {
        const x = width - (column + 1) * cell + cell / 2;
        const y = row * cell + cell / 2;
        const dx = (peakX - x) / rx;
        const dy = (y - peakY) / ry;
        const radius = Math.sqrt(dx * dx + dy * dy);
        const alpha =
          Math.exp(-(field.power === 1 ? radius : radius ** field.power)) *
          field.peak;
        if (alpha < FLOOR) continue;
        // Column 0 is the rightmost cell, so it is the last pixel in the row.
        const at = (row * cols + (cols - 1 - column)) * 4;
        data[at] = field.glow[0];
        data[at + 1] = field.glow[1];
        data[at + 2] = field.glow[2];
        data[at + 3] = Math.round(alpha * 255);
      }
    }
    context.putImageData(image, 0, 0);
  }
}
